import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { customerResolutionModeSchema } from "./customer-resolution-mode";

/**
 * Create Sales Order Draft Step
 *
 * Creates staged customer purchase orders (sales order drafts) from an inbound email.
 * Processes each document classified as a purchase order independently, creating a separate
 * staged PO for each document.
 *
 * This step:
 * - If `documents` input is provided, only processes those specific documents
 *   (recommended: pass filtered output from extract step to avoid stale OCR results)
 * - If `documents` is omitted, falls back to processing all PURCHASE_ORDER documents from email
 * - For each PO document:
 *   - Extracts purchase order data from that specific document using OCR results
 *   - Identifies the customer using embeddings or fallback methods
 *   - Matches line items to customer products
 *   - Creates StagedCustomerPurchaseOrder record with processing errors if customer not found
 * - Records memory bank note usages for each staged PO
 * - Returns array of all created staged purchase orders
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/order-management/
 */
export const createSalesOrderDraftStep = defineStepConfig({
  type: "create_sales_order_draft",
  name: "Create Sales Order Draft",
  description:
    "Extract purchase order data from classified documents and create sales order drafts",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Superseded by the workflow-native draft-sales-order flow. Use 'Create Draft Sales Order', which persists a relational ar_draft_sales_orders header + line items (stable line ids) instead of the JSONB staged-PO line array.",
    replacedBy: "create_draft_sales_order",
  },
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email containing purchase order documents",
    ),
    // Optional filtered document list from upstream extract step.
    // When provided, only these documents are processed (avoids re-processing
    // documents with stale OCR results from previous workflow runs).
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "Filtered document IDs to process (from extract step). If omitted, all PO documents from the email are processed.",
      ),
    // Email origin metadata — used to detect replies and skip order creation
    originType: z
      .string()
      .optional()
      .describe(
        'Email origin classification: "external", "self_sent", or "reply_to_outbound"',
      ),
    conversationId: z
      .string()
      .optional()
      .describe(
        "Microsoft Graph conversation/thread ID for tracing replies to orders",
      ),
  }),

  outputSchema: z.object({
    stagedOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of all staged purchase orders created from this email"),
    firstStagedOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "UUID of the first staged purchase order (convenience output for single-PO flows). Undefined when no PO documents were found.",
      ),
    inboundEmailId: z
      .string()
      .uuid()
      .describe("UUID of the inbound email that was processed"),
    // Reply flow outputs — populated when originType is "reply_to_outbound"
    // and a related order is found in the conversation thread
    entityType: z
      .string()
      .optional()
      .describe(
        "Entity type of the related order (e.g., AR_STAGED_SALES_ORDER) — set in reply flow",
      ),
    entityId: z
      .string()
      .optional()
      .describe(
        "Entity ID of the related order — set in reply flow for the background agent",
      ),
    customerReplyText: z
      .string()
      .optional()
      .describe(
        "Stripped reply text from the customer (quoted thread removed) — set in reply flow",
      ),
  }),

  configSchema: z
    .object({
      skipClassificationCheck: z
        .boolean()
        .optional()
        .describe(
          "Skip document classification checks (used for legacy processing paths)",
        ),
      customerId: z
        .uuid()
        .optional()
        .describe(
          "Pre-identified customer ID to use instead of running customer matching",
        ),
      customerResolutionMode: customerResolutionModeSchema
        .optional()
        .describe(
          "Strategy for identifying the customer from extracted PO data. " +
            '"embeddings" uses Pinecone vector search + LLM disambiguation (default). ' +
            '"location_first" resolves customer + org from ship-to address via joint trigram search. ' +
            '"known" is a passthrough when customerId is set in config.',
        ),
    })
    .refine(
      (config) =>
        config.customerResolutionMode !== "known" || !!config.customerId,
      {
        message: 'customerResolutionMode "known" requires customerId to be set',
        path: ["customerId"],
      },
    ),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
